import { useState, useEffect, useRef } from "react";
import "../../styles/Navigation.css";
import {
  Menu,
  X,
  FileText,
  LogIn,
  UserPlus,
  User,
  ChevronDown,
  MessageSquare,
  DollarSign,
  BookOpen,
  Video,
  LogOut,
  LayoutDashboard,
  Sun,
  Moon,
  Snowflake,
  ShieldAlert,
  Home,
  Key,
} from "lucide-react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { useTheme } from "../../lib/ThemeContext";
import logoNms06 from "../../assets/logo_nms06.png";

export default function Navigation() {
  const [isOpen, setIsOpen] = useState(false); // menu mobile
  const [isScrolled, setIsScrolled] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 1200 : false
  );
  const [isServicesOpen, setIsServicesOpen] = useState(false);

  const userMenuRef = useRef(null);

  // Auth (avec isAdmin intégré)
  const { user, isAuthenticated, isAdmin, logout } = useAuth();

  const {
    isDark,
    toggleTheme,
    seasonalEnabled,
    toggleSeasonal,
    currentSeason,
  } = useTheme();

  const navigate = useNavigate();

  const userRole = user?.role || null;
  const isTenant = userRole === "tenant";
  const isLandlord = userRole === "landlord";

  // 👉 À adapter selon ton modèle utilisateur si besoin
  const isAdherent = !!user?.isAdherent;

  // --- Scroll ---
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // --- Resize ---
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 1200;
      setIsMobile(mobile);
      if (!mobile) {
        setIsOpen(false);
        setIsUserMenuOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // --- Fermer le menu utilisateur quand on clique ailleurs ---
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // --- Liens publics (Forum → Réseau NMS) ---
  const publicLinks = [
    { to: "/association-story", icon: BookOpen, label: "Histoire" },
    { to: "/videos", icon: Video, label: "Vidéos" },
    { to: "/direct", icon: Video, label: "Direct" },
    { to: "/pere-noel", icon: MessageSquare, label: "Père Noël" },
    { to: "/pere-noel/premium", icon: DollarSign, label: "Plans Noël 🎁" },
    { to: "/actions", icon: FileText, label: "Actus" },
    { to: "/reseau", icon: MessageSquare, label: "Réseau NMS" },
    { to: "/donation", icon: DollarSign, label: "Don" },
  ];

  const handleLogout = () => {
    logout();
    navigate("/");
    setIsOpen(false);
    setIsUserMenuOpen(false);
    setIsServicesOpen(false);
  };

  const closeMenus = () => {
    setIsOpen(false);
    setIsUserMenuOpen(false);
  };

  const closeAllOverlays = () => {
    setIsOpen(false);
    setIsUserMenuOpen(false);
    setIsServicesOpen(false);
  };

  const NavItem = ({ item, mobile = false }) => (
    <NavLink
      to={item.to}
      className={({ isActive }) =>
        `${mobile ? "nav__mobile-menu-item" : "nav__menu-item"} ${
          isActive ? "active" : ""
        }`
      }
      onClick={closeMenus}
    >
      <item.icon className="nav__menu-item-icon" size={18} />
      {item.label}
    </NavLink>
  );

  return (
    <nav className={`nav ${isScrolled ? "scrolled" : "transparent"}`}>
      {/* LOGO */}
      <div className="nav__brand-wrapper">
        <Link to="/" className="nav__logo-link" onClick={closeAllOverlays}>
          <img src={logoNms06} alt="Logo NMS 06" className="nav__logo-img" />
          <span className="nav__brand-name">
            NOUVELLES MÉTHODES SOLIDAIRES 06
          </span>
        </Link>
      </div>

      {/* MENU DESKTOP */}
      {!isMobile && (
        <div className="nav__menu--desktop">
          {publicLinks.map((link) => (
            <NavItem key={link.to} item={link} />
          ))}

          {/* 🔥 Lien Adhérer visible si NON adhérent */}
          {(!isAuthenticated || (isAuthenticated && !isAdherent)) && (
            <NavLink
              to="/adhesion"
              className="nav__menu-item nav__menu-item--adhesion"
              onClick={closeAllOverlays}
            >
              <ShieldAlert className="nav__menu-item-icon" size={18} />
              Adhérer
            </NavLink>
          )}

          {/* Services */}
          <button
            type="button"
            className="nav__menu-item nav__menu-item--services"
            onClick={() => setIsServicesOpen(true)}
          >
            <FileText className="nav__menu-item-icon" size={18} />
            Services
          </button>

          {/* Séparateur */}
          <div className="nav__separator"></div>

          {/* UTILISATEUR */}
          {isAuthenticated ? (
            <div className="nav__user" ref={userMenuRef}>
              <button
                className="nav__menu-item user-trigger highlight-user"
                type="button"
                onClick={() =>
                  setIsUserMenuOpen((prevIsOpen) => !prevIsOpen)
                }
              >
                <User size={18} />
                <span className="user-name">
                  {user?.firstName || "Mon Compte"}
                </span>
                <ChevronDown size={16} />
              </button>

              {isUserMenuOpen && (
                <div className="nav__user-menu">
                  <div className="menu-header">
                    Bonjour, {user?.firstName || "Membre"}
                  </div>

                  <Link
                    to="/profile"
                    className="nav__user-menu-item"
                    onClick={closeAllOverlays}
                  >
                    <User size={16} /> Mon Profil
                  </Link>

                  <Link
                    to="/my-grievances"
                    className="nav__user-menu-item"
                    onClick={closeAllOverlays}
                  >
                    <ShieldAlert size={16} /> Mes Doléances
                  </Link>

                  {isAdmin && (
                    <Link
                      to="/admin"
                      className="nav__user-menu-item admin-link"
                      onClick={closeAllOverlays}
                    >
                      <LayoutDashboard size={16} /> Dashboard Admin
                    </Link>
                  )}

                  <div className="menu-divider"></div>

                  <button
                    onClick={handleLogout}
                    className="nav__user-menu-item logout"
                    type="button"
                  >
                    <LogOut size={16} /> Se déconnecter
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="nav__auth-buttons">
              <Link
                to="/register"
                className="nav__btn-auth register"
                onClick={closeAllOverlays}
              >
                <UserPlus size={16} /> Inscription
              </Link>
              <Link
                to="/login"
                className="nav__btn-auth login"
                onClick={closeAllOverlays}
              >
                <LogIn size={16} /> Connexion
              </Link>
            </div>
          )}
        </div>
      )}

      {/* ACTIONS : Thème / Neige / Mobile */}
      <div className="nav__actions">
        {currentSeason && (
          <button
            onClick={toggleSeasonal}
            className={`theme-btn ${seasonalEnabled ? "active-season" : ""}`}
            title="Neige"
            type="button"
          >
            <Snowflake size={18} />
          </button>
        )}

        <button
          onClick={toggleTheme}
          className="theme-btn"
          title="Thème"
          type="button"
        >
          {isDark ? <Sun size={18} /> : <Moon size={18} />}
        </button>

        {isMobile && (
          <button
            className="nav__mobile-button"
            onClick={() => setIsOpen((prev) => !prev)}
            type="button"
          >
            {isOpen ? <X /> : <Menu />}
          </button>
        )}
      </div>

      {/* MOBILE MENU */}
      {isMobile && (
        <>
          <div
            className={`nav__overlay ${isOpen ? "open" : ""}`}
            onClick={() => setIsOpen(false)}
          />
          <div className={`nav__mobile-menu ${isOpen ? "open" : ""}`}>
            <div className="nav__mobile-header">
              <span>Menu</span>
              <button onClick={() => setIsOpen(false)} type="button">
                <X />
              </button>
            </div>

            <div className="nav__mobile-scroll">
              {publicLinks.map((link) => (
                <NavItem key={link.to} item={link} mobile={true} />
              ))}

              {/* 🔥 Lien Adhérer aussi sur mobile si non adhérent */}
              {(!isAuthenticated || (isAuthenticated && !isAdherent)) && (
                <NavLink
                  to="/adhesion"
                  className="nav__mobile-menu-item"
                  onClick={closeAllOverlays}
                >
                  <ShieldAlert size={18} /> Adhérer
                </NavLink>
              )}

              <button
                type="button"
                className="nav__mobile-menu-item"
                onClick={() => {
                  setIsServicesOpen(true);
                  setIsOpen(false);
                }}
              >
                <FileText size={18} /> Services
              </button>

              <div className="nav__divider">Espace Membre</div>

              {isAuthenticated ? (
                <>
                  <div className="nav__mobile-user-info">
                    <User size={20} /> {user?.firstName || "Membre"}
                  </div>

                  <Link
                    to="/profile"
                    className="nav__mobile-menu-item"
                    onClick={closeAllOverlays}
                  >
                    Mon Profil
                  </Link>

                  <Link
                    to="/my-grievances"
                    className="nav__mobile-menu-item"
                    onClick={closeAllOverlays}
                  >
                    Mes Doléances
                  </Link>

                  {isAdmin && (
                    <Link
                      to="/admin"
                      className="nav__mobile-menu-item"
                      onClick={closeAllOverlays}
                    >
                      Dashboard Admin
                    </Link>
                  )}

                  <button
                    onClick={handleLogout}
                    className="nav__mobile-menu-item logout"
                    type="button"
                  >
                    <LogOut size={18} /> Déconnexion
                  </button>
                </>
              ) : (
                <div className="mobile-auth-stack">
                  <Link
                    to="/register"
                    className="nav__mobile-menu-item"
                    onClick={closeAllOverlays}
                  >
                    <UserPlus size={18} /> S'inscrire
                  </Link>
                  <Link
                    to="/login"
                    className="nav__mobile-menu-item highlight"
                    onClick={closeAllOverlays}
                  >
                    <LogIn size={18} /> Connexion
                  </Link>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* OVERLAY SERVICES */}
      {isServicesOpen && (
        <>
          <div
            className="services-overlay-backdrop"
            onClick={() => setIsServicesOpen(false)}
          />
          <div className="services-overlay-panel">
            <div className="services-overlay-content">
              <div className="services-overlay-header">
                <h2>Services NMS 06</h2>
                <button
                  type="button"
                  className="services-overlay-close"
                  onClick={() => setIsServicesOpen(false)}
                >
                  <X size={20} />
                </button>
              </div>

              <p className="services-overlay-subtitle">
                Choisissez un service pour continuer :
              </p>

              <div className="services-grid">
                <Link
                  to="/letter-generator"
                  className="service-card"
                  onClick={closeAllOverlays}
                >
                  <FileText size={28} />
                  <h3>Générateur de lettres</h3>
                  <p>Créez vos courriers officiels en quelques clics.</p>
                </Link>

                <Link
                  to="/chat-ia"
                  className="service-card"
                  onClick={closeAllOverlays}
                >
                  <MessageSquare size={28} />
                  <h3>Chat IA Logement</h3>
                  <p>Assistant intelligent pour vos questions logement.</p>
                </Link>

                {(isTenant || isAdmin) && (
                  <Link
                    to="/tenant-space"
                    className="service-card"
                    onClick={closeAllOverlays}
                  >
                    <Home size={28} />
                    <h3>Espace Locataire</h3>
                    <p>Vos démarches et doléances.</p>
                  </Link>
                )}

                {(isLandlord || isAdmin) && (
                  <Link
                    to="/actions"
                    className="service-card"
                    onClick={closeAllOverlays}
                  >
                    <Key size={28} />
                    <h3>Espace Propriétaire</h3>
                    <p>Gestion, échanges, médiation.</p>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </nav>
  );
}
